import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { usersService, UserProfile, UserPreferences } from '../services/users.service';
import { useAuth } from '../hooks/useAuth';

type TabType = 'profile' | 'security' | 'privacy' | 'notifications' | 'video' | 'matching' | 'account';

export const SettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, refreshAuth } = useAuth();
  const [activeTab, setActiveTab] = useState<TabType>('profile');
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [preferences, setPreferences] = useState<UserPreferences | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Profile form
  const [email, setEmail] = useState('');

  // Password form
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');

  // Delete account
  const [deletePassword, setDeletePassword] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const [profileData, preferencesData] = await Promise.all([
        usersService.getProfile(),
        usersService.getPreferences(),
      ]);

      setProfile(profileData);
      setPreferences(preferencesData);
      setEmail(profileData.email);
    } catch (err: any) {
      setError(err.message || 'Failed to load settings');
    } finally {
      setIsLoading(false);
    }
  };

  const showSuccess = (message: string) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(null), 5000);
  };

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    try {
      await usersService.updateProfile({ email });
      showSuccess('Profile updated successfully');
      await refreshAuth();
      await loadData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update profile');
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError('');
    setError(null);

    if (newPassword.length < 8) {
      setPasswordError('Password must be at least 8 characters');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }

    try {
      await usersService.changePassword({ currentPassword, newPassword });
      showSuccess('Password changed successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to change password');
    }
  };

  const handleUpdatePreferences = async (updates: Partial<UserPreferences>) => {
    setError(null);

    try {
      // Convert null values to undefined for the DTO
      const dto: any = {};
      for (const [key, value] of Object.entries(updates)) {
        if (value !== null) {
          dto[key] = value;
        }
      }

      const updated = await usersService.updatePreferences(dto);
      setPreferences(updated);
      showSuccess('Preferences updated');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update preferences');
    }
  };

  const handleDeleteAccount = async () => {
    setError(null);

    if (!deletePassword) {
      setError('Please enter your password to confirm');
      return;
    }

    try {
      await usersService.deleteAccount(deletePassword);
      // Logout and redirect
      localStorage.clear();
      navigate('/login');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete account');
    }
  };

  const handleExportData = async () => {
    try {
      const data = await usersService.exportData();
      const blob = new Blob([JSON.stringify(data, null, 2)], {
        type: 'application/json',
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `vibetalk-data-${Date.now()}.json`;
      link.click();
      URL.revokeObjectURL(url);
      showSuccess('Data exported successfully');
    } catch (err: any) {
      setError(err.message || 'Failed to export data');
    }
  };

  if (isLoading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingCard}>
          <p>Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.content}>
        {/* Header */}
        <div style={styles.header}>
          <button onClick={() => navigate(-1)} style={styles.backButton}>
            ← Back
          </button>
          <h1 style={styles.pageTitle}>Settings</h1>
        </div>

        {/* Success/Error Messages */}
        {successMessage && (
          <div style={styles.successBanner}>{successMessage}</div>
        )}
        {error && <div style={styles.errorBanner}>{error}</div>}

        {/* Tabs */}
        <div style={styles.tabs}>
          {[
            { id: 'profile', label: 'Profile' },
            { id: 'security', label: 'Security' },
            { id: 'privacy', label: 'Privacy' },
            { id: 'notifications', label: 'Notifications' },
            { id: 'video', label: 'Video' },
            { id: 'matching', label: 'Matching' },
            { id: 'account', label: 'Account' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as TabType)}
              style={{
                ...styles.tab,
                ...(activeTab === tab.id ? styles.activeTab : {}),
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div style={styles.card}>
          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <div>
              <h2 style={styles.sectionTitle}>Profile Settings</h2>
              <form onSubmit={handleUpdateProfile}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Email</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    style={styles.input}
                    required
                  />
                  {!profile?.isEmailVerified && (
                    <p style={styles.helperText}>⚠️ Email not verified</p>
                  )}
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Account Status</label>
                  <div style={styles.statusBadges}>
                    {user?.isPremium && (
                      <span style={styles.premiumBadge}>Premium</span>
                    )}
                    {user?.isAdmin && (
                      <span style={styles.adminBadge}>Admin</span>
                    )}
                  </div>
                </div>

                <button type="submit" style={styles.primaryButton}>
                  Save Changes
                </button>
              </form>
            </div>
          )}

          {/* Security Tab */}
          {activeTab === 'security' && (
            <div>
              <h2 style={styles.sectionTitle}>Change Password</h2>
              <form onSubmit={handleChangePassword}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Current Password</label>
                  <input
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    style={styles.input}
                    required
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>New Password</label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    style={styles.input}
                    minLength={8}
                    required
                  />
                  <p style={styles.helperText}>
                    Must be at least 8 characters
                  </p>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Confirm New Password</label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    style={styles.input}
                    required
                  />
                </div>

                {passwordError && (
                  <p style={styles.errorText}>{passwordError}</p>
                )}

                <button type="submit" style={styles.primaryButton}>
                  Change Password
                </button>
              </form>
            </div>
          )}

          {/* Privacy Tab */}
          {activeTab === 'privacy' && preferences && (
            <div>
              <h2 style={styles.sectionTitle}>Privacy Settings</h2>

              <div style={styles.settingItem}>
                <div>
                  <div style={styles.settingTitle}>IP Protection</div>
                  <div style={styles.settingDescription}>
                    Force all traffic through TURN relay to hide your IP address
                    (may increase latency)
                  </div>
                </div>
                <label style={styles.switch}>
                  <input
                    type="checkbox"
                    checked={preferences.enableIPProtection}
                    onChange={(e) =>
                      handleUpdatePreferences({
                        enableIPProtection: e.target.checked,
                      })
                    }
                  />
                  <span style={styles.slider}></span>
                </label>
              </div>

              <div style={styles.settingItem}>
                <div>
                  <div style={styles.settingTitle}>Show Online Status</div>
                  <div style={styles.settingDescription}>
                    Let others see when you're online
                  </div>
                </div>
                <label style={styles.switch}>
                  <input
                    type="checkbox"
                    checked={preferences.showOnlineStatus}
                    onChange={(e) =>
                      handleUpdatePreferences({
                        showOnlineStatus: e.target.checked,
                      })
                    }
                  />
                  <span style={styles.slider}></span>
                </label>
              </div>

              <div style={styles.settingItem}>
                <div>
                  <div style={styles.settingTitle}>Data Collection</div>
                  <div style={styles.settingDescription}>
                    Allow anonymous analytics to help improve the platform
                  </div>
                </div>
                <label style={styles.switch}>
                  <input
                    type="checkbox"
                    checked={preferences.allowDataCollection}
                    onChange={(e) =>
                      handleUpdatePreferences({
                        allowDataCollection: e.target.checked,
                      })
                    }
                  />
                  <span style={styles.slider}></span>
                </label>
              </div>
            </div>
          )}

          {/* Notifications Tab */}
          {activeTab === 'notifications' && preferences && (
            <div>
              <h2 style={styles.sectionTitle}>Notification Preferences</h2>

              <div style={styles.settingItem}>
                <div>
                  <div style={styles.settingTitle}>Email Notifications</div>
                  <div style={styles.settingDescription}>
                    Receive important updates and notifications via email
                  </div>
                </div>
                <label style={styles.switch}>
                  <input
                    type="checkbox"
                    checked={preferences.emailNotifications}
                    onChange={(e) =>
                      handleUpdatePreferences({
                        emailNotifications: e.target.checked,
                      })
                    }
                  />
                  <span style={styles.slider}></span>
                </label>
              </div>

              <div style={styles.settingItem}>
                <div>
                  <div style={styles.settingTitle}>Marketing Emails</div>
                  <div style={styles.settingDescription}>
                    Receive news, tips, and promotional offers
                  </div>
                </div>
                <label style={styles.switch}>
                  <input
                    type="checkbox"
                    checked={preferences.marketingEmails}
                    onChange={(e) =>
                      handleUpdatePreferences({
                        marketingEmails: e.target.checked,
                      })
                    }
                  />
                  <span style={styles.slider}></span>
                </label>
              </div>
            </div>
          )}

          {/* Video Tab */}
          {activeTab === 'video' && preferences && (
            <div>
              <h2 style={styles.sectionTitle}>Video Settings</h2>

              <div style={styles.formGroup}>
                <label style={styles.label}>Default Video Quality</label>
                <select
                  value={preferences.defaultVideoQuality}
                  onChange={(e) =>
                    handleUpdatePreferences({
                      defaultVideoQuality: e.target.value,
                    })
                  }
                  style={styles.select}
                >
                  <option value="auto">Auto</option>
                  <option value="720p">720p</option>
                  <option value="1080p">1080p</option>
                </select>
                <p style={styles.helperText}>
                  Auto adjusts based on network conditions
                </p>
              </div>

              <div style={styles.settingItem}>
                <div>
                  <div style={styles.settingTitle}>Enable Audio by Default</div>
                  <div style={styles.settingDescription}>
                    Start with microphone enabled when joining calls
                  </div>
                </div>
                <label style={styles.switch}>
                  <input
                    type="checkbox"
                    checked={preferences.defaultAudioEnabled}
                    onChange={(e) =>
                      handleUpdatePreferences({
                        defaultAudioEnabled: e.target.checked,
                      })
                    }
                  />
                  <span style={styles.slider}></span>
                </label>
              </div>

              <div style={styles.settingItem}>
                <div>
                  <div style={styles.settingTitle}>Enable Video by Default</div>
                  <div style={styles.settingDescription}>
                    Start with camera enabled when joining calls
                  </div>
                </div>
                <label style={styles.switch}>
                  <input
                    type="checkbox"
                    checked={preferences.defaultVideoEnabled}
                    onChange={(e) =>
                      handleUpdatePreferences({
                        defaultVideoEnabled: e.target.checked,
                      })
                    }
                  />
                  <span style={styles.slider}></span>
                </label>
              </div>
            </div>
          )}

          {/* Matching Tab */}
          {activeTab === 'matching' && preferences && (
            <div>
              <h2 style={styles.sectionTitle}>Matching Preferences</h2>

              <div style={styles.formGroup}>
                <label style={styles.label}>Preferred Conversation Topics</label>
                <p style={styles.helperText}>
                  Select the types of conversation prompts you prefer
                </p>
                <div style={styles.checkboxGroup}>
                  {['Gratitude', 'Vulnerability', 'Dreams', 'Fears'].map((category) => (
                    <label key={category} style={styles.checkboxLabel}>
                      <input
                        type="checkbox"
                        checked={preferences.preferredCategories?.includes(category) || false}
                        onChange={(e) => {
                          const current = preferences.preferredCategories || [];
                          const updated = e.target.checked
                            ? [...current, category]
                            : current.filter((c) => c !== category);
                          handleUpdatePreferences({ preferredCategories: updated });
                        }}
                        style={styles.checkbox}
                      />
                      <span style={styles.checkboxText}>{category}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div style={styles.settingItem}>
                <div>
                  <div style={styles.settingTitle}>Auto-Connect After Rating</div>
                  <div style={styles.settingDescription}>
                    Automatically connect to a new chat after rating your previous conversation
                  </div>
                </div>
                <label style={styles.switch}>
                  <input
                    type="checkbox"
                    checked={preferences.autoConnectAfterRating}
                    onChange={(e) =>
                      handleUpdatePreferences({
                        autoConnectAfterRating: e.target.checked,
                      })
                    }
                  />
                  <span style={styles.slider}></span>
                </label>
              </div>
            </div>
          )}

          {/* Account Tab */}
          {activeTab === 'account' && (
            <div>
              <h2 style={styles.sectionTitle}>Account Management</h2>

              {/* Export Data */}
              <div style={styles.dangerZone}>
                <h3 style={styles.dangerTitle}>Export Your Data</h3>
                <p style={styles.dangerDescription}>
                  Download all your data in JSON format (GDPR compliant)
                </p>
                <button onClick={handleExportData} style={styles.secondaryButton}>
                  Export Data
                </button>
              </div>

              {/* Delete Account */}
              <div style={styles.dangerZone}>
                <h3 style={styles.dangerTitle}>Delete Account</h3>
                <p style={styles.dangerDescription}>
                  Permanently delete your account and all associated data. This
                  action cannot be undone.
                </p>

                {!showDeleteConfirm ? (
                  <button
                    onClick={() => setShowDeleteConfirm(true)}
                    style={styles.dangerButton}
                  >
                    Delete Account
                  </button>
                ) : (
                  <div style={styles.deleteConfirm}>
                    <p style={styles.warningText}>
                      ⚠️ Are you sure? This action is permanent!
                    </p>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>
                        Enter your password to confirm
                      </label>
                      <input
                        type="password"
                        value={deletePassword}
                        onChange={(e) => setDeletePassword(e.target.value)}
                        style={styles.input}
                        placeholder="Your password"
                      />
                    </div>
                    <div style={styles.buttonGroup}>
                      <button
                        onClick={handleDeleteAccount}
                        style={styles.dangerButton}
                      >
                        Yes, Delete My Account
                      </button>
                      <button
                        onClick={() => {
                          setShowDeleteConfirm(false);
                          setDeletePassword('');
                        }}
                        style={styles.secondaryButton}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    minHeight: '100vh',
    backgroundColor: 'var(--color-soft-cream)',
    padding: '2rem',
  },
  content: {
    maxWidth: '900px',
    margin: '0 auto',
  },
  loadingCard: {
    backgroundColor: 'white',
    borderRadius: 'var(--radius-lg)',
    padding: '3rem',
    textAlign: 'center',
    boxShadow: 'var(--shadow-md)',
  },
  header: {
    position: 'relative',
    marginBottom: '2rem',
    textAlign: 'center',
  },
  backButton: {
    position: 'absolute',
    left: 0,
    top: 0,
    padding: '0.5rem 1rem',
    backgroundColor: 'white',
    border: '1px solid var(--color-gray-300)',
    borderRadius: 'var(--radius-md)',
    cursor: 'pointer',
    fontSize: '1rem',
    color: 'var(--color-dark-navy)',
    fontWeight: '500',
  },
  pageTitle: {
    fontSize: '2.5rem',
    fontWeight: '700',
    color: 'var(--color-deep-purple)',
    margin: 0,
  },
  successBanner: {
    backgroundColor: '#d1fae5',
    color: '#065f46',
    padding: '1rem',
    borderRadius: 'var(--radius-md)',
    marginBottom: '1rem',
    textAlign: 'center',
    fontWeight: '500',
  },
  errorBanner: {
    backgroundColor: '#fee2e2',
    color: '#dc2626',
    padding: '1rem',
    borderRadius: 'var(--radius-md)',
    marginBottom: '1rem',
    textAlign: 'center',
    fontWeight: '500',
  },
  tabs: {
    display: 'flex',
    gap: '0.5rem',
    marginBottom: '2rem',
    backgroundColor: 'white',
    padding: '0.5rem',
    borderRadius: 'var(--radius-lg)',
    boxShadow: 'var(--shadow-sm)',
    overflowX: 'auto',
  },
  tab: {
    padding: '0.75rem 1.5rem',
    backgroundColor: 'transparent',
    border: 'none',
    borderRadius: 'var(--radius-md)',
    fontSize: '1rem',
    fontWeight: '500',
    color: 'var(--color-gray-600)',
    cursor: 'pointer',
    transition: 'all 0.2s',
    whiteSpace: 'nowrap',
  },
  activeTab: {
    backgroundColor: 'var(--color-warm-orange)',
    color: 'white',
  },
  card: {
    backgroundColor: 'white',
    borderRadius: 'var(--radius-lg)',
    padding: '2rem',
    boxShadow: 'var(--shadow-md)',
  },
  sectionTitle: {
    fontSize: '1.5rem',
    fontWeight: '600',
    color: 'var(--color-deep-purple)',
    marginTop: 0,
    marginBottom: '1.5rem',
  },
  formGroup: {
    marginBottom: '1.5rem',
  },
  label: {
    display: 'block',
    fontSize: '0.9rem',
    fontWeight: '600',
    color: 'var(--color-gray-700)',
    marginBottom: '0.5rem',
  },
  input: {
    width: '100%',
    padding: '0.75rem',
    fontSize: '1rem',
    border: '1px solid var(--color-gray-300)',
    borderRadius: 'var(--radius-md)',
    boxSizing: 'border-box',
  },
  select: {
    width: '100%',
    padding: '0.75rem',
    fontSize: '1rem',
    border: '1px solid var(--color-gray-300)',
    borderRadius: 'var(--radius-md)',
    boxSizing: 'border-box',
  },
  helperText: {
    fontSize: '0.85rem',
    color: 'var(--color-gray-500)',
    marginTop: '0.25rem',
    marginBottom: 0,
  },
  errorText: {
    fontSize: '0.875rem',
    color: '#dc2626',
    marginTop: '0.5rem',
    marginBottom: '1rem',
  },
  statusBadges: {
    display: 'flex',
    gap: '0.5rem',
  },
  premiumBadge: {
    padding: '0.25rem 0.75rem',
    backgroundColor: 'var(--color-warm-orange)',
    color: 'white',
    borderRadius: 'var(--radius-sm)',
    fontSize: '0.875rem',
    fontWeight: '600',
  },
  adminBadge: {
    padding: '0.25rem 0.75rem',
    backgroundColor: 'var(--color-deep-purple)',
    color: 'white',
    borderRadius: 'var(--radius-sm)',
    fontSize: '0.875rem',
    fontWeight: '600',
  },
  primaryButton: {
    padding: '0.75rem 2rem',
    backgroundColor: 'var(--color-warm-orange)',
    color: 'white',
    border: 'none',
    borderRadius: 'var(--radius-md)',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  },
  secondaryButton: {
    padding: '0.75rem 2rem',
    backgroundColor: 'var(--color-gray-200)',
    color: 'var(--color-dark-navy)',
    border: 'none',
    borderRadius: 'var(--radius-md)',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  },
  dangerButton: {
    padding: '0.75rem 2rem',
    backgroundColor: '#dc2626',
    color: 'white',
    border: 'none',
    borderRadius: 'var(--radius-md)',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  },
  settingItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '1.5rem',
    backgroundColor: 'var(--color-gray-50)',
    borderRadius: 'var(--radius-md)',
    marginBottom: '1rem',
  },
  settingTitle: {
    fontSize: '1rem',
    fontWeight: '600',
    color: 'var(--color-gray-700)',
    marginBottom: '0.25rem',
  },
  settingDescription: {
    fontSize: '0.875rem',
    color: 'var(--color-gray-500)',
    maxWidth: '600px',
  },
  switch: {
    position: 'relative',
    display: 'inline-block',
    width: '60px',
    height: '34px',
  },
  slider: {
    position: 'absolute',
    cursor: 'pointer',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: '#ccc',
    transition: '0.4s',
    borderRadius: '34px',
  },
  dangerZone: {
    border: '2px solid #fee2e2',
    borderRadius: 'var(--radius-md)',
    padding: '1.5rem',
    marginBottom: '1.5rem',
  },
  dangerTitle: {
    fontSize: '1.1rem',
    fontWeight: '600',
    color: '#dc2626',
    marginTop: 0,
    marginBottom: '0.5rem',
  },
  dangerDescription: {
    fontSize: '0.9rem',
    color: 'var(--color-gray-600)',
    marginBottom: '1rem',
  },
  deleteConfirm: {
    marginTop: '1rem',
  },
  warningText: {
    fontSize: '0.95rem',
    fontWeight: '600',
    color: '#dc2626',
    marginBottom: '1rem',
  },
  buttonGroup: {
    display: 'flex',
    gap: '1rem',
    marginTop: '1rem',
  },
  checkboxGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
    marginTop: '0.75rem',
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    padding: '0.75rem',
    backgroundColor: 'var(--color-gray-50)',
    borderRadius: 'var(--radius-md)',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  },
  checkbox: {
    width: '20px',
    height: '20px',
    marginRight: '0.75rem',
    cursor: 'pointer',
    accentColor: 'var(--color-warm-orange)',
  },
  checkboxText: {
    fontSize: '1rem',
    color: 'var(--color-gray-700)',
    fontWeight: '500',
  },
};

// Add CSS for switch slider animation
const styleSheet = document.styleSheets[0];
styleSheet.insertRule(`
  input:checked + span {
    background-color: var(--color-warm-orange) !important;
  }
`, styleSheet.cssRules.length);

styleSheet.insertRule(`
  input:checked + span:before {
    transform: translateX(26px);
  }
`, styleSheet.cssRules.length);

styleSheet.insertRule(`
  input[type="checkbox"] {
    opacity: 0;
    width: 0;
    height: 0;
  }
`, styleSheet.cssRules.length);

styleSheet.insertRule(`
  span:before {
    position: absolute;
    content: "";
    height: 26px;
    width: 26px;
    left: 4px;
    bottom: 4px;
    background-color: white;
    transition: 0.4s;
    border-radius: 50%;
  }
`, styleSheet.cssRules.length);
